import { useFetch } from "~vue/api";
import { getAcceptedFileTypes } from "~vue/utils/fileUpload";
import { normalizeMarkdownFiles } from "~vue/utils/markdownFileUpload";
import { WAF_ERROR_CAUSE } from "~vue/utils/wafDetection";
import { computed, inject, ref } from "vue";

const DOCUMENT_TYPES = [
    "text/plain",
    "text/csv",
    "text/markdown",
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
];

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

const CONSERVATIVE_DEFAULT_FILE_SIZE_LIMIT = 3 * 1024 * 1024;
const CONSERVATIVE_FILE_SIZE_LIMITS = {
    limits: {},
    default: CONSERVATIVE_DEFAULT_FILE_SIZE_LIMIT,
};

// TODO: This takes much of the logic from ChatMessageControl and trims it down.
// We eventually want to hook up the logic here to ChatMessageControl
export const useFileUpload = ({ uploadFinishedCallback, unsupportedFileCallback, oversizedFileCallback, errorCallback } = {}) => {
    const organizationDetails = inject("organization_details", {});
    const userDetails = inject("user_details", {});
    // Read-only profile sections can render outside the app shell, where `globalProperties` is not
    // provided.
    const { $feedbackMailto } = inject("globalProperties", {});
    const isUploading = ref(false);
    const acceptedFileTypes = computed(() => getAcceptedFileTypes(!!organizationDetails.is_image_upload_enabled));

    const isUploadEnabled = computed(() => {
        return userDetails.value.is_document_upload_enabled;
    });

    const {
        data: fileSizeLimitsData,
        error: fileSizeLimitsError,
        execute: fetchFileSizeLimits,
    } = useFetch("/api/chat/document/file-size-limits", { immediate: false }).get().json();
    let fileSizeLimitsPromise;

    const getFileSizeLimits = () => {
        if (!fileSizeLimitsPromise) {
            fileSizeLimitsPromise = fetchFileSizeLimits()
                .then(() => {
                    if (fileSizeLimitsError.value || !fileSizeLimitsData.value) {
                        return CONSERVATIVE_FILE_SIZE_LIMITS;
                    }
                    return {
                        limits: fileSizeLimitsData.value.limits ?? {},
                        default: fileSizeLimitsData.value.default ?? CONSERVATIVE_DEFAULT_FILE_SIZE_LIMIT,
                    };
                })
                .catch(() => CONSERVATIVE_FILE_SIZE_LIMITS);
        }
        return fileSizeLimitsPromise;
    };

    const validateFiles = async (files) => {
        let filesForUpload = normalizeMarkdownFiles(Array.from(files), [...DOCUMENT_TYPES, ...IMAGE_TYPES]);
        let SUPPORTED_FILE_TYPES = [...DOCUMENT_TYPES];
        if (organizationDetails.is_image_upload_enabled) {
            SUPPORTED_FILE_TYPES.push(...IMAGE_TYPES);
        }

        const unsupportedFiles = filesForUpload.filter((file) => !SUPPORTED_FILE_TYPES.includes(file.type));

        if (unsupportedFiles.length > 0) {
            unsupportedFileCallback?.(unsupportedFiles);
            filesForUpload = filesForUpload.filter((file) => !unsupportedFiles.includes(file));
        }

        if (filesForUpload.length === 0) {
            return filesForUpload;
        }

        const fileSizeLimits = await getFileSizeLimits();
        const oversizedFiles = filesForUpload.flatMap((file) => {
            const limit = fileSizeLimits.limits[file.type] ?? fileSizeLimits.default;
            return file.size > limit ? [{ file, limit }] : [];
        });
        if (oversizedFiles.length > 0) {
            oversizedFileCallback?.(oversizedFiles);
            const oversizedFileSet = new Set(oversizedFiles.map(({ file }) => file));
            filesForUpload = filesForUpload.filter((file) => !oversizedFileSet.has(file));
        }

        return filesForUpload;
    };

    const uploadFile = async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        const { data: fileUpload, error, statusCode } = await useFetch("/api/chat/document/upload").post(formData).json();
        if (error.value) {
            if (statusCode.value === 403) {
                // duplicated from ChatMessageControls logic, future refactor will consolidate the logic into this composable
                // https://linear.app/elrond/issue/PLT-309/clean-up-and-consolidate-logic-for-chatmessagecontrol-upload-file
                throw new Error(`Your file upload was blocked by our security system. Please [contact support](${$feedbackMailto}) if you believe this is an error.`, {
                    cause: WAF_ERROR_CAUSE,
                });
            }
            throw new Error(error.value);
        }
        return fileUpload.value;
    };

    const uploadFiles = async (files) => {
        isUploading.value = true;
        try {
            const validatedFiles = await validateFiles(files);
            if (validatedFiles.length === 0) {
                throw new Error("No valid files to upload");
            }

            const fileUploadPromises = validatedFiles.map((file) => uploadFile(file));

            const result = await Promise.allSettled(fileUploadPromises);
            const uploadedFiles = [];
            result.forEach(({ status, value, reason }) => {
                if (status === "rejected") {
                    errorCallback?.(reason);
                } else {
                    if (value.error) {
                        errorCallback?.(value.error);
                        return;
                    }
                    uploadedFiles.push(value);
                    uploadFinishedCallback?.();
                }
            });

            return { uploadedFiles };
        } finally {
            isUploading.value = false;
        }
    };

    return { uploadFiles, acceptedFileTypes, isUploading, isUploadEnabled };
};
